import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ApprovalGateway, FourEyesContext } from "../api/client";
import type { PendingApproval } from "../api/four-eyes";

// The four-eyes UX boundary (ADR 0015 §6). When the API signals approval-required,
// the ApiClient calls requestApproval(); this context turns that into visible UI
// state (a pending-approval banner) and resolves once the originator supplies the
// approval id that a SECOND authorized user produced from their approver inbox.
//
// AC1 wires the plumbing end-to-end with an explicit prompt; AC6 replaces the
// prompt with the full inbox-driven handshake. The console NEVER self-approves —
// the API enforces approver ≠ executor; this UX only reflects it.
interface ApprovalContextValue extends ApprovalGateway {
  pending: PendingApproval | null;
}

const ApprovalContext = createContext<ApprovalContextValue | null>(null);

export interface ApprovalProviderProps {
  children: ReactNode;
  /** How the approval id is obtained (AC1: a prompt; AC6: the inbox flow). */
  resolveApprovalId?: (ctx: FourEyesContext) => Promise<string | null>;
}

export function ApprovalProvider({ children, resolveApprovalId }: ApprovalProviderProps) {
  const [pending, setPending] = useState<PendingApproval | null>(null);
  const resolver = useRef(resolveApprovalId);
  resolver.current = resolveApprovalId;

  const requestApproval = useCallback(async (ctx: FourEyesContext): Promise<string | null> => {
    setPending({ action: `${ctx.method} ${ctx.path}`, status: "REQUESTED" });
    try {
      const id = resolver.current
        ? await resolver.current(ctx)
        : // Default AC1 affordance: a deliberate, explicit advisor action — never
          // automatic. Replaced by the inbox flow in AC6.
          (globalThis.prompt?.("Bu işlem ikinci onay gerektiriyor. Onay kimliğini girin:") ?? null);
      setPending(id ? { action: `${ctx.method} ${ctx.path}`, id, status: "APPROVED" } : null);
      return id && id.trim() ? id.trim() : null;
    } catch {
      setPending(null);
      return null;
    }
  }, []);

  const value = useMemo<ApprovalContextValue>(
    () => ({ requestApproval, pending }),
    [requestApproval, pending],
  );
  return <ApprovalContext.Provider value={value}>{children}</ApprovalContext.Provider>;
}

export function useApprovals(): ApprovalContextValue {
  const ctx = useContext(ApprovalContext);
  if (!ctx) throw new Error("useApprovals must be used within an ApprovalProvider");
  return ctx;
}
